<template>
  <div>
    <div v-if="manualModeHelper.isManualModeActive || manualModeHelper.isReplyWindowModeActive" id="manual-mode" class="alert alert-warning mt-3">
      <div v-if="manualModeHelper.isManualModeActive">
        <p>
          <strong>Automated text messaging is turned off</strong> so that you may free text with the participant.
          <b-icon
            v-b-popover.hover.top="'Inbound text messages will not generate automated responses or incidents. Outbound automated messages will queue and send when automated mode is re-enabled or when you leave the participant\'s inbox.'"
            icon="info-circle-fill"
          />
          <span class="sr-only">Inbound text messages will not generate automated responses or incidents. Outbound automated messages will queue and send when automated mode is re-enabled or when you leave the participant's inbox.</span>
        </p>

        <!--- TODO: Add info about paused convo here if there is one --->
      </div>
      <div v-if="manualModeHelper.isReplyWindowModeActive">
        <p>Now waiting for a reply from the participant until <strong>{{ manualModeHelper.replyWindowModeExpiration }}</strong>.</p>

        <ul v-if="replyWindowConfiguration.createIncident || replyWindowConfiguration.respondWithMessage">
          <li>Once the patient replies, automated messaging will resume.</li>
          <li v-if="replyWindowConfiguration.createIncident">An incident (<strong>{{replyWindowConfiguration.incidentClassification}}</strong>) will be generated.</li>
          <li v-if="replyWindowConfiguration.respondWithMessage">A reply message will be sent to the participant.</li>
        </ul>
        <p v-else>Once the patient replies, automated messaging will resume.</p>

        <p>Any conversation that starts prior will immediately end the current reply window session.</p>
      </div>
      <div id="mode-switch-actions" class="action-list">
        <span v-if="!manualModeHelper.isAutomatedModeActive">
          <button class="btn btn-link" @click.prevent="enableAutomaticMode">Return to automated mode</button>
        </span>
        <span v-if="replyWindowEnabled && !manualModeHelper.isReplyWindowModeActive" class="ml-2">
          <span class="sr-only">If you want to be notified when the participant responds to your message, you may enable a reply window.</span>
          <button class="btn btn-link" @click.prevent="enableReplyWindow" :disabled="hasPendingTexts">
            Start a reply window
            <em v-if="hasPendingTexts">(Not available, there are pending texts)</em>
            <b-icon
              v-else
              v-b-popover.hover.top="'If you want to be notified when the participant responds to your message, you may enable a reply window.'"
              icon="info-circle-fill"
              class="ml-1"
            />
          </button>
        </span>
      </div>
    </div>

    <div v-else-if="manualModeEnabled && manualModeHelper.isAutomatedModeActive" id="auto-mode" class="text-success font-italic mt-3">
      <div v-if="manualModeHelper.isConversationActive">
        <p>
          <strong>{{ manualModeHelper.conversationName }}</strong> conversation in progress until
          <strong>{{ manualModeHelper.conversationExpiration }}</strong>.
          <b-icon
            v-b-popover.hover.top="'Sending a message to the participant will pause this conversation and stop automated messaging until switched back to automated mode.'"
            icon="info-circle-fill"
          />
          <span class="sr-only">Sending a message to the participant will pause this conversation and stop automated messaging until switched back to automated mode.</span>
        </p>
      </div>
      <div v-else>
        <p>
          <span>Any incoming texts from this participant will be processed by the study's configured message handlers.</span>
          <b-icon
            v-b-popover.hover.top="'Sending a message to the participant will stop automated messaging until switched back to automated mode.'"
            icon="info-circle-fill"
          />
          <span class="sr-only">Sending a message to the participant will stop automated messaging until switched back to automated mode.</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import replyWindowHelper from "@/Inbox/helpers/replyWindow";
import manualModeHelper from "@/Inbox/helpers/manualMode";

export default {
  name: "MessagingModeSwitcher",
  props: {
    store: {
      type: Object,
      required: true
    },
    manualModeEnabled: {
      type: Boolean,
      default: false,
    },
    replyWindowEnabled: {
      type: Boolean,
      default: false,
    },
    replyWindowConfiguration: {
      type: [Object, Array],
      default() {
        return {};
      }
    },
    hasPendingTexts: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      replyWindowHelper,
      manualModeHelper
    }
  },
  mounted() {
    // force updating for the sake of the relative timer which isn't easy to make reactive
    setInterval(() => {
      this.$forceUpdate();
    }, 30 * 1000);
  },
  methods: {
    async enableAutomaticMode() {
      await this.store.enableAutomaticMode();
    },
    getReplyWindowDuration() {
      if (this.replyWindowConfiguration.mode === "duration") {
        return replyWindowHelper.getDurationFromHoursAndMinutes(this.replyWindowConfiguration.hours, this.replyWindowConfiguration.minutes);
      }
      return replyWindowHelper.getDurationFromTimeString(this.replyWindowConfiguration.stopTime);
    },
    async enableReplyWindow() {
      const pausedConversation = await this.store.getPausedConversation();
      if (pausedConversation != null) {
        if (confirm(`The ${pausedConversation.source_name} conversation is currently paused. Starting a reply window will stop that conversation.\n\nAre you sure you want to continue?`)) {
          await this.store.enableReplyWindowMode(this.getReplyWindowDuration());
        }
      } else {
        await this.store.enableReplyWindowMode(this.getReplyWindowDuration());
      }
    },
    async enableManualMode() {
      await this.store.enableManualMode();
    },
  }
}
</script>

<style scoped>
.action-list {
  margin-left: -0.75rem;
}
</style>
